import React from "react";
import clsx from "clsx";
import { PlaceDetailsOUT } from "../api-client";
import { StepIconProps } from "@material-ui/core";
import { globalStore } from "store/GlobalStore";

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export const REQUIREMENTS_URL =
  "https://concular.notion.site/Ifc-Model-Requirements-27ac3a4b5a1d469c8b9657d84ffced68";

export const IFC_ENTITIES_URL =
  "https://www.notion.so/concular/Werden-bestimmte-IFC-Entit-ten-beim-Import-vom-System-ignoriert-a14fcb3ea9564e09967115016f18f2c0?pvs=4";

export const CERT_EXPORT_ALERT_URL =
  "https://concular.notion.site/kobilanzierung-f-r-Zertifizierungen-0fac08a5324b495b853952a493308b95?pvs=4";

export const SUPPORTING_DOCS =
  "https://www.qng.info/app/uploads/2023/07/QNG_Handbuch_Anlage-1-Siegelvarianten_v1-4.pdf";

export const CIRCULARITY_GUIDE_URL =
  "https://concular.notion.site/Circularity-Performance-Index-CPX-8aa764b1b29344eabf96466f68c23ed8";

export const BNB_DOCUMENT =
  "https://www.nachhaltigesbauen.de/fileadmin/pdf/Nutzungsdauer_Bauteile/BNB_Nutzungsdauern_von_Bauteilen_2017-02-24.pdf";

export const CIRCULARITY_DOCUMENT_URL =
  "https://concular.notion.site/Circularity-Performance-Index-CPX-8aa764b1b29344eabf96466f68c23ed8?pvs=4";

export const RESIDENTIAL_BUILDING_FILE =
  "https://concular-retool-assets.s3.eu-central-1.amazonaws.com/concore/b6_operational_energy/Betriebsenergie%2BBerechnung_Wohngeb%C3%A4ude.xlsx";

export const NON_RESIDENTIAL_BUILDING_FILE =
  "https://concular-retool-assets.s3.eu-central-1.amazonaws.com/concore/b6_operational_energy/Betriebsenergie%2BBerechnung_nichtwohngeb%C3%A4ude.xlsx";

export const RULES_USER_DOCS_URL =
  "https://concular.notion.site/Wie-funktioniert-der-Regel-Set-Editor-1f36f0c6558c80169a22d5f53e2d06d1?pvs=4";

export const PORTFOLIO_URL = "portfolio-dashboard";
export const URBAN_MINING_HUB_URL = "urban-mining";
export const CIRCULAR_ASSET_PORTFOLIO = "assets-portfolio";
export const VARIANT_COMPARISON_DOCS =
  "https://concular.notion.site/Wie-funktioniert-der-Variantenvergleich-1856f0c6558c8038a31fe2034de9d71e?pvs=4";
export const HOTSPOT_ANALYZER_DOCS =
  "https://concular.notion.site/Wie-funktioniert-der-CO-Hotspot-Analyzer-1cb6f0c6558c807d87d0e3946ea78437?pvs=4";

export const placeDetailsToString = (item: PlaceDetailsOUT) => {
  return item.street ||
    item.latitude ||
    item.longitude ||
    item.country_code ||
    item.place_id
    ? `${item.street ?? ""} ${item.house_number ?? ""} ${
        item.postal_code ?? ""
      } ${item.region ?? ""} ${item.country_code ?? ""}`.trim()
    : "";
};

export const isMobileSize = () => window.innerWidth <= 640;
export const isTabletSize = () => window.innerWidth <= 768;

export function slugify(str: string | number, separator = "-") {
  return str
    .toString()
    .normalize("NFD")
    .toLowerCase()
    .trim()
    .replace(/\s+/g, separator);
}

export default function checkEmailValidation(value: string) {
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value);
}

export function skeleton(className?: string) {
  return (
    <div className="animate-pulse">
      <div
        className={`h-80 w-full bg-gray-200 rounded-lg dark:bg-gray-700 mb-2.5 ${className}`}
      ></div>
    </div>
  );
}

export const StepperCustomCircle = (props: StepIconProps) => {
  return (
    <div
      className={clsx(
        "flex rounded-full w-8 h-8 justify-center items-center self-center",
        {
          "border border-indigo-600 text-indigo-700": props.active,
          "bg-indigo-600 border border-indigo-600 text-white": props.completed,
          "bg-gray-100 border border-gray-100 text-gray-500":
            !props.completed && !props.active,
        }
      )}
    >
      {props.icon}
    </div>
  );
};

export const unitPower = (unit: string) => {
  return (
    <span>
      {unit.endsWith("3") || unit.endsWith("2") ? (
        <>
          {unit.slice(0, unit.length - 1)}
          <sup>{unit[unit.length - 1]}</sup>
        </>
      ) : (
        unit
      )}
    </span>
  );
};

export const numberCustomFormat = (num: number) => {
  if (num === 0) {
    return "0.00";
  } else if (num < 0.01) {
    return num.toFixed(6).replace(/0+$/, "");
  } else {
    return num.toFixed(2);
  }
};

/**
 * Check the status if it's undefined or null and includes failed, return true
 */
export function isFailedStatus(status: string | null | undefined) {
  return status?.includes("failed") ?? false;
}

export function getModuleStringTotal(certification: string | undefined | null) {
  if (certification === "DGNB 2018") {
    return "moduleString_total_2018";
  } else if (certification?.includes("Level")) {
    return "moduleString_levels";
  } else {
    return "moduleString_total";
  }
}

export function getModuleStringB4(certification: string | undefined) {
  if (certification === "DGNB 2018") {
    return "moduleString_b4_2018";
  } else {
    return "moduleString_b4";
  }
}

export function roundNumber(num: number | string) {
  return Math.round((Number(num) + Number.EPSILON) * 100) / 100;
}

export const IFC_CLASSES = [
  { id: "IfcBeam", name: "IfcBeam" },
  { id: "IfcBearing", name: "IfcBearing" },
  { id: "IfcBuildingElementProxy", name: "IfcBuildingElementProxy" },
  { id: "IfcChimney", name: "IfcChimney" },
  { id: "IfcColumn", name: "IfcColumn" },
  { id: "IfcCovering", name: "IfcCovering" },
  { id: "IfcCurtainWall", name: "IfcCurtainWall" },
  { id: "IfcDeepFoundation", name: "IfcDeepFoundation" },
  { id: "IfcDoor", name: "IfcDoor" },
  { id: "IfcFooting", name: "IfcFooting" },
  { id: "IfcMember", name: "IfcMember" },
  { id: "IfcPlate", name: "IfcPlate" },
  { id: "IfcRailing", name: "IfcRailing" },
  { id: "IcRamp", name: "IcRamp" },
  { id: "IfcRampFlight", name: "IfcRampFlight" },
  { id: "IfcRoof", name: "IfcRoof" },
  { id: "IfcShadingDevice", name: "IfcShadingDevice" },
  { id: "IfcSlab", name: "IfcSlab" },
  { id: "IfcStair", name: "IfcStair" },
  { id: "IfcStairFlight", name: "IfcStairFlight" },
  { id: "IfcWall", name: "IfcWall" },
  { id: "IfcWindow", name: "IfcWindow" },
];

const viteBuildName = import.meta.env.VITE_BUILD_NAME;
export const IS_CONCULAR_ONE =
  viteBuildName !== undefined && viteBuildName.includes("concular-one");

export const IS_CIRCULAR_LCA =
  viteBuildName === "" || viteBuildName === undefined;

export const IS_CIRCULAR_ASSET =
  viteBuildName !== undefined && viteBuildName.includes("circular-asset");

export const CONCULAR_ONE_URL = "https://www.concular.one/";

export const ORG_ACCORDION_STATUS_KEY = "org-accordion-status";

export const CONSULTATION_CALENDAR_URL =
  "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ1QdAbDcf1q0RlSYqQmH7bLlK99A8PZut2CVGDEVpyrr8COJeyIPMCHfLF42G9Ph8Uzg1wvBToj";
export const REQUEST_OFFER_URL = "https://concular.de/angebot-anfordern/";

export const SOFTWARE_EMAIL = "software@concular.com";
export const PRICES_OVERVIEW_URL = "https://circularlca.de/preise/";

export const BOOLEAN_OPTIONS = [
  {
    id: "true",
    name: "True",
    name_de: "Ja",
    value: true,
  },
  {
    id: "false",
    name: "False",
    name_de: "Nein",
    value: false,
  },
];

export const getBooleanName = (value: null | boolean) => {
  const foundedItem = BOOLEAN_OPTIONS.find((item) => item.value == value);
  return globalStore.currentLanguage === "de"
    ? foundedItem?.name_de
    : foundedItem?.name;
};

export const IconBricks = (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 13.5882L7.64103 15.7059M2 13.5882V18.8824L7.64103 21M2 13.5882L9.57234 10.6935M7.64103 15.7059V21M7.64103 15.7059L11.3142 14.2899M7.64103 21L13.267 18.3219M9.57234 10.6935L11.3142 14.2899M9.57234 10.6935L7.64102 6.70588L10.2051 3M11.3142 14.2899L13.267 18.3219M13.267 18.3219L14.3077 20.4706M14.3077 20.4706L17.3846 17.8235M14.3077 20.4706L19.4359 18.8824L22 16.2353M10.2051 3L17.3846 17.8235M10.2051 3H15.8462L22 16.2353M17.3846 17.8235L22 16.2353"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const USER_DOCS_URL =
  "https://concular.notion.site/Dokumentation-220d5b48b5ba43c58c37bbeea09d7364?pvs=4";

export const areListsEqualUnordered = (list1: string[], list2: string[]) => {
  if (list1.length !== list2.length) return false;
  const sortedA = [...list1].sort((a, b) => a.localeCompare(b));
  const sortedB = [...list2].sort((a, b) => a.localeCompare(b));
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

export const IconObject = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 2.5L16.6667 6.25V13.75L10 17.5L3.33337 13.75V6.25L10 2.5Z"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

export const IconProduct = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M16.6667 6.25L10 2.5L3.33337 6.25M16.6667 6.25V13.75L10 17.5L3.33337 13.75V10M16.6667 6.25L10 10L3.33337 6.25M3.33337 6.25V10M3.33337 10L10 13.75L16.6667 10"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

export function getPercentageStatusColor(percentage: number) {
  if (percentage >= 75) {
    return "#22c55e";
  } else if (percentage >= 25) {
    return "#eab308";
  } else {
    return "#EF4444";
  }
}
